import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'

import { listGroupConfigs } from '../../../api/groupConfigs.js'
import {
  deleteSegmentation,
  listSegmentations,
} from '../../../api/segmentations.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function ApplicationSegmentsListPage() {
  const { applicationId } = useOutletContext()
  const { confirm, toast } = useFeedback()
  const [groupConfigId, setGroupConfigId] = useState('')
  const [groupConfigs, setGroupConfigs] = useState([])
  const [state, setState] = useState({
    status: 'loading',
    data: [],
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    listGroupConfigs(applicationId)
      .then((list) => {
        if (cancelled) return
        setGroupConfigs(list || [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [applicationId])

  useEffect(() => {
    let cancelled = false

    setState({
      status: 'loading',
      data: [],
      error: null,
    })

    const opts = { includeDefaults: false }
    if (groupConfigId !== '') opts.groupConfigId = Number(groupConfigId)

    listSegmentations(applicationId, opts)
      .then((data) => {
        if (cancelled) return
        const sorted = [...data].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        setState({
          status: 'ok',
          data: sorted,
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          data: [],
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, groupConfigId])

  async function handleDelete(segmentationId) {
    const confirmed = await confirm({
      title: 'Delete segmentation',
      message: 'Delete this segmentation? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteSegmentation(applicationId, segmentationId)
      const nextData = state.data.filter((item) => String(item.id) !== String(segmentationId))
      setState({
        status: 'ok',
        data: nextData,
        error: null,
      })
    } catch (e) {
      toast({
        type: 'error',
        message: String(e?.message || e),
      })
    }
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">Segments</div>
        </div>
        <div className="buttonGroup">
          {state.status === 'ok' && groupConfigs.length > 0 ? (
            <select
              className="textInput selectFilter"
              value={groupConfigId}
              onChange={(e) => setGroupConfigId(e.target.value)}
              aria-label="Filter by group config"
            >
              <option value="">All group configs</option>
              {groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          ) : null}
          <Link to={`/applications/${applicationId}/segments/new`} className="button buttonPrimary">
            Create
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <p className="muted inlineMessage">
          Loading segmentations...
        </p>
      ) : null}

      {state.status === 'error' ? (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      ) : null}

      {state.status === 'ok' && state.data.length === 0 ? (
        <p className="muted inlineMessage">
          No segmentations yet.
        </p>
      ) : null}

      {state.status === 'ok' && state.data.length > 0 ? (
        <table className="table" aria-label="Segmentations list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Group config</th>
              <th>Created</th>
              <th className="alignRight"></th>
            </tr>
          </thead>
          <tbody>
            {state.data.map((seg) => (
              <tr key={seg.id}>
                <td>{seg.name || 'Unnamed'}</td>
                <td>{seg.groupConfig?.name ?? ''}</td>
                <td>{formatDate(seg.updatedAt || seg.createdAt)}</td>
                <td className="alignRight">
                  <span className="tableActions">
                    <Link
                      to={`/applications/${applicationId}/segments/${seg.id}`}
                      className="button buttonGhost"
                    >
                      View
                    </Link>
                    <button
                      type="button"
                      className="button buttonGhost buttonDanger"
                      onClick={() => handleDelete(seg.id)}
                      disabled
                    >
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  )
}

